// Copying out of the PDF to the system clipboard (AC-CLIP-1): selected lines as
// plain text in reading order, an image as a PNG at its own resolution. The
// clipboard is the computer's own; nothing leaves the browser otherwise.
import type { EngineClient } from '../engine/worker/client.ts';
import { openForViewing } from './pdfjs.ts';
import * as pdfjs from 'pdfjs-dist';
import type { Item, Selection } from './model.ts';
import { rememberWritten } from './picture.ts';

/** Pictures larger than this on a side are scaled down to it (canvas limits). */
const MAX_SIDE = 8192;

/** The selected lines in reading order: top to bottom, then left to right (user space: larger y is higher). */
export function spansInReadingOrder(items: Item[]): Extract<Item, { kind: 'span' }>[] {
  const at = (it: Extract<Item, { kind: 'span' }>) => it.pieces[0].quads[0][0];
  return items.filter((it): it is Extract<Item, { kind: 'span' }> => it.kind === 'span').sort((a, b) => at(b)[1] - at(a)[1] || at(a)[0] - at(b)[0]);
}

/** The selected lines' text in reading order. */
export const linesInReadingOrder = (items: Item[]): string[] => spansInReadingOrder(items).map((it) => it.line.text);

/** The image as a PNG: the one-page PDF the engine made of it, drawn at one pixel per image pixel, transparency kept. */
async function imagePng(engine: EngineClient, page: number, imageIndex: number): Promise<Blob> {
  const res = await engine.call({ type: 'imageDoc', pageIndex: page, imageIndex });
  const handle = openForViewing(new Uint8Array(res.bytes));
  try {
    const doc = await handle.promise;
    const pg = await doc.getPage(1);
    const scale = Math.min(1, MAX_SIDE / Math.max(res.width, res.height));
    const viewport = pg.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    await pg.render({ canvas, canvasContext: canvas.getContext('2d')!, viewport, background: 'rgba(0, 0, 0, 0)', annotationMode: pdfjs.AnnotationMode.DISABLE }).promise;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    canvas.width = 0;
    if (!blob) throw new Error('the picture could not be made');
    return blob;
  } finally {
    await handle.destroy();
  }
}

/**
 * Puts the selection on the clipboard. Returns what was copied, for the message. The clipboard holds one picture
 * at most, so several images are refused.
 */
export async function copySelection(engine: EngineClient, sel: Selection): Promise<string> {
  const lines = linesInReadingOrder(sel.items);
  const images = sel.items.filter((it): it is Extract<Item, { kind: 'image' }> => it.kind === 'image');
  if (images.length > 1) throw new Error('the clipboard holds one picture at a time: select one image to copy it');
  const data: Record<string, Blob> = {};
  if (lines.length) data['text/plain'] = new Blob([lines.join('\n')], { type: 'text/plain' });
  if (images.length) data['image/png'] = await imagePng(engine, sel.page, images[0].image.index);
  await navigator.clipboard.write([new ClipboardItem(data)]);
  // So a paste of it pastes FlexPDF's own copy (fonts and vectors kept), not a picture of it.
  rememberWritten(lines.length ? lines.join('\n') : undefined, data['image/png']);
  const what = [lines.length === 1 ? 'the line' : lines.length ? `${lines.length} lines` : '', images.length ? 'the image' : ''].filter(Boolean).join(' and ');
  return `Copied ${what} to the clipboard.`;
}
